import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import FilePreviewDialog from '../components/FilePreviewDialog';
import FileShareDialog from '../components/FileShareDialog';
import NotificationBell from '../components/NotificationBell';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import AgreementModal from '../components/AgreementModal';
import RCAFormModal from '../components/RCAFormModal';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { 
  ArrowLeft,
  Upload,
  Download,
  Users,
  Clock,
  Calendar,
  FileText,
  Image,
  File,
  Plus,
  UserPlus,
  CheckSquare,
  Square,
  BarChart3,
  Settings,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Check,
  AlertCircle,
  FileSpreadsheet,
  FileImage,
  Share2,
  FileSignature
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [allResearchers, setAllResearchers] = useState([]);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Agreement states
  const [ndaModalOpen, setNdaModalOpen] = useState(false);
  const [rcaModalOpen, setRcaModalOpen] = useState(false);
  const [rcaFormOpen, setRcaFormOpen] = useState(false);
  const [agreements, setAgreements] = useState({ nda: [], rca: [] });
  const [ndaContent, setNdaContent] = useState('');
  const [rcaContent, setRcaContent] = useState('');

  // File upload states
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Task creation state
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: [],
    priority: 'medium',
    due_date: '',
    dependencies: []
  });

  // Collaborator invitation state
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [collaboratorRole, setCollaboratorRole] = useState('collaborator');

  // Documents states
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);

  // Computed values
  const isOwner = myProfile && project && project.owner_id === myProfile.id;

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchMyProfile();
  }, [user, navigate]);

  useEffect(() => {
    if (myProfile && projectId) {
      fetchProject();
      fetchDocuments();
    }
  }, [myProfile, projectId]);

  useEffect(() => {
    if (projectId) {
      fetchAgreements();
      loadAgreementContent();
    }
  }, [projectId, language]);

  const fetchAgreements = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/agreements/project/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setAgreements(data);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
    }
  };

  const loadAgreementContent = async () => {
    try {
      // Import agreement templates (we'll use the backend to get them)
      const ndaResponse = await fetch(`${BACKEND_URL}/api/agreements/content/NDA?language=${language}`);
      // For RCA, include project_id to get filled template if it exists
      const rcaResponse = await fetch(`${BACKEND_URL}/api/agreements/content/RCA?language=${language}&project_id=${projectId}`);
      
      if (ndaResponse.ok) {
        const ndaData = await ndaResponse.json();
        setNdaContent(ndaData.content);
      }
      
      if (rcaResponse.ok) {
        const rcaData = await rcaResponse.json();
        setRcaContent(rcaData.content);
      }
    } catch (error) {
      console.error('Error loading agreement content:', error);
    }
  };

  const fetchMyProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/`);
      if (response.ok) {
        const profiles = await response.json();
        const my = profiles.find(p => p.email === user.email);
        setMyProfile(my);
        setAllResearchers(profiles);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}?user_id=${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setProject(data);
      } else if (response.status === 403) {
        toast({
          title: t('accessDenied'),
          description: t('noPermissionToView'),
          variant: "destructive"
        });
        navigate('/projects');
      } else if (response.status === 404) {
        toast({
          title: t('projectNotFound'),
          description: t('projectDoesNotExist'),
          variant: "destructive"
        });
        navigate('/projects');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast({
        title: t('errorTitle'),
        description: t('failedToLoadProject'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch documents for the project
  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/project/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  // Upload a new document
  const handleDocumentUpload = async () => {
    if (!documentFile) {
      toast({
        title: t('noFileSelected'),
        description: t('selectFileToUpload'),
        variant: "destructive"
      });
      return;
    }

    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);
      formData.append('project_id', projectId);
      formData.append('owner_id', myProfile.id);
      formData.append('title', documentFile.name);

      const response = await fetch(`${BACKEND_URL}/api/documents/create`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast({
          title: t('documentUploaded'),
          description: t('documentUploadedSuccess'),
        });
        setDocumentFile(null);
        setDocumentDialogOpen(false);
        fetchDocuments(); // Refresh document list
      } else {
        throw new Error(t('failedToUploadDocument'));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: t('uploadFailed'),
        description: t('failedToUploadDocument'),
        variant: "destructive"
      });
    } finally {
      setUploadingDocument(false);
    }
  };

  // Delete a document
  const handleDeleteDocument = async (documentKey) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/${documentKey}?user_id=${myProfile.id}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        toast({
          title: t('documentDeleted'),
          description: t('documentDeletedSuccess'),
        });
        setSelectedDocument(null);
        fetchDocuments();
      } else {
        throw new Error(t('failedToUploadDocument'));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: t('deleteFailed'),
        description: t('failedToUploadDocument'),
        variant: "destructive"
      });
    }
  };

  // Open document in editor
  const handleOpenDocument = (doc) => {
    setSelectedDocument(doc);
  };

  // Handle document rename
  const handleDocumentRename = (documentKey, newTitle) => {
    setDocuments(prevDocs => 
      prevDocs.map(doc => 
        doc.key === documentKey ? { ...doc, title: newTitle } : doc
      )
    );
    if (selectedDocument && selectedDocument.key === documentKey) {
      setSelectedDocument({ ...selectedDocument, title: newTitle });
    }
  };

  // Handle document delete from preview
  const handleDocumentDeleteFromPreview = (documentKey) => {
    setDocuments(prevDocs => prevDocs.filter(doc => doc.key !== documentKey));
    setSelectedDocument(null);
  };

  const toggleTaskCompletion = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'completed' ? 'in_progress' : 'completed';
      const completionPercentage = newStatus === 'completed' ? 100 : 0;
      
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/tasks/${taskId}?user_id=${myProfile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          completion_percentage: completionPercentage
        })
      });

      if (response.ok) {
        toast({
          title: newStatus === 'completed' ? t('taskCompleted') : t('taskReopened'),
          description: newStatus === 'completed' 
            ? t('taskMarkedAsDone')
            : t('taskMarkedAsIncomplete'),
        });
        fetchProject(); // Refresh to show updated task
      } else {
        throw new Error(t('couldNotUpdateTask'));
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: t('updateFailed'),
        description: t('couldNotUpdateTask'),
        variant: "destructive"
      });
    }
  };

  const deleteTask = async (taskId, taskTitle) => {
    if (!window.confirm(t('confirmDeleteTask') + ` "${taskTitle}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/tasks/${taskId}?user_id=${myProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('taskDeleted'),
          description: t('taskDeletedMessage'),
        });
        fetchProject(); // Refresh to show updated task list
      } else {
        throw new Error(t('couldNotDeleteTask'));
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: t('deleteFailed'),
        description: t('couldNotDeleteTask'),
        variant: "destructive"
      });
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (uploadFiles.length === 0) {
      toast({
        title: t('errorTitle'),
        description: t('selectAtLeastOneFile'),
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      for (const file of uploadFiles) {
        // Check file size (100MB limit)
        if (file.size > 100 * 1024 * 1024) {
          toast({
            title: t('fileTooLarge'),
            description: `${file.name} ${t('exceedsLimit')}`,
            variant: "destructive"
          });
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', myProfile.id);
        if (uploadDescription) {
          formData.append('description', uploadDescription);
        }

        const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to upload ${file.name}`);
        }
      }

      toast({
        title: t('filesUploadedSuccess'),
        description: `${t('uploadedFiles')} ${uploadFiles.length} ${t('fileCount')}`,
      });

      setUploadFiles([]);
      setUploadDescription('');
      setUploadDialogOpen(false);
      fetchProject(); // Refresh project data

    } catch (error) {
      toast({
        title: t('uploadFailed'),
        description: t('someFilesFailedToUpload'),
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleInviteCollaborators = async () => {
    if (selectedCollaborators.length === 0) {
      toast({
        title: t('errorTitle'),
        description: t('selectAtLeastOneResearcher'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/collaborators?user_id=${myProfile.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          researcher_ids: selectedCollaborators,
          role: collaboratorRole
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: t('collaboratorsInvited'),
          description: data.message,
        });
        setSelectedCollaborators([]);
        setInviteDialogOpen(false);
        fetchProject(); // Refresh project data
      } else {
        throw new Error(t('failedToInviteCollaborators'));
      }
    } catch (error) {
      toast({
        title: t('errorTitle'),
        description: t('failedToInviteCollaborators'),
        variant: "destructive"
      });
    }
  };

  const getFileIcon = (fileType) => {
    const icons = {
      'pdf': FileText,
      'doc': FileText,
      'docx': FileText,
      'xlsx': FileSpreadsheet,
      'xls': FileSpreadsheet,
      'png': FileImage,
      'jpg': FileImage,
      'jpeg': FileImage,
      'gif': FileImage,
      'default': File
    };
    const IconComponent = icons[fileType] || icons.default;
    return <IconComponent className="h-6 w-6" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      'planning': 'bg-blue-100 text-blue-700',
      'active': 'bg-green-100 text-green-700',
      'on_hold': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'todo': 'bg-gray-100 text-gray-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'review': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-700',
      'medium': 'bg-yellow-100 text-yellow-700',
      'high': 'bg-red-100 text-red-700'
    };
    return colors[priority] || 'bg-gray-100 text-gray-700';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isProjectOwner = () => {
    return myProfile && project && project.owner_id === myProfile.id;
  };

  const isProjectAdmin = () => {
    if (!myProfile || !project) return false;
    
    // Owner is always admin
    if (project.owner_id === myProfile.id) return true;
    
    // Check if user is admin collaborator
    const userCollaborator = project.collaborators?.find(c => c.researcher_id === myProfile.id);
    return userCollaborator && ['admin'].includes(userCollaborator.role);
  };

  const canEditProject = () => {
    if (!project || !myProfile) return false;
    if (project.owner_id === myProfile.id) return true;
    
    const userCollaborator = project.collaborators?.find(c => c.researcher_id === myProfile.id);
    return userCollaborator && ['admin'].includes(userCollaborator.role);
  };

  const handlePreviewFile = (file) => {
    setSelectedFile(file);
    setPreviewDialogOpen(true);
  };

  const handleShareFile = (file) => {
    setSelectedFile(file);
    setShareDialogOpen(true);
  };

  const handleDeleteFile = async (fileId, filename) => {
    if (!confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${fileId}?user_id=${myProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('fileDeleted'),
          description: `${t('successfullyDeleted')} ${filename}`,
        });
        fetchProject(); // Refresh project data
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || t('deleteFailed'));
      }
    } catch (error) {
      toast({
        title: t('deleteFailed'), 
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleRemoveCollaborator = async (collaboratorId, collaboratorName) => {
    if (!confirm(`Are you sure you want to remove "${collaboratorName}" from this project?`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/collaborators/${collaboratorId}?user_id=${myProfile.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('collaboratorRemoved'),
          description: `${t('successfullyRemoved')} ${collaboratorName}`,
        });
        fetchProject(); // Refresh project data
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || t('removeFailed'));
      }
    } catch (error) {
      toast({
        title: t('removeFailed'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleChangeCollaboratorRole = async (collaboratorId, collaboratorName, newRole) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/collaborators/${collaboratorId}/role?user_id=${myProfile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast({
          title: t('roleUpdated'),
          description: `${t('changedRoleTo')} ${collaboratorName} ${newRole}`,
        });
        fetchProject(); // Refresh project data
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || t('roleUpdateFailed'));
      }
    } catch (error) {
      toast({
        title: t('roleUpdateFailed'),
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      // Use direct link download to bypass fetch interceptors
      const downloadUrl = `${BACKEND_URL}/api/projects/${projectId}/files/${fileId}/download?user_id=${myProfile.id}`;
      
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: t('downloadFailed'),
        description: t('unableToDownload'),
        variant: "destructive"
      });
    }
  };

  const canInviteCollaborators = () => {
    if (!project || !myProfile) return false;
    if (project.owner_id === myProfile.id) return true;
    
    const userCollaborator = project.collaborators?.find(c => c.researcher_id === myProfile.id);
    return userCollaborator && ['admin'].includes(userCollaborator.role);
  };

  const handleCreateTask = async () => {
    if (!taskForm.title) {
      toast({
        title: t('errorTitle'),
        description: t('taskTitleRequired'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/tasks?user_id=${myProfile.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskForm)
      });

      if (response.ok) {
        toast({
          title: t('taskCreated'),
          description: t('taskAddedToProject'),
        });
        setTaskForm({
          title: '',
          description: '',
          assigned_to: [],
          priority: 'medium',
          due_date: '',
          dependencies: []
        });
        setTaskDialogOpen(false);
        fetchProject(); // Refresh project data
      } else {
        throw new Error(t('failedToCreateTask'));
      }
    } catch (error) {
      toast({
        title: t('errorTitle'),
        description: t('failedToCreateTask'),
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingProject')}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('projectNotFound')}</h1>
          <p className="text-gray-600 mb-4">{t('projectDoesNotExist')}</p>
          <Button onClick={() => navigate('/projects')}>
            {t('backToProjects')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/projects')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('backToProjects')}
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{project.title}</h1>
                <Badge className={getStatusColor(project.status)}>
                  {project.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <NotificationBell />
              {canEditProject() && (
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-1" />
                  {t('settings')}
                </Button>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Project Overview */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t('overview')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {project.description && (
                      <p className="text-gray-700">{project.description}</p>
                    )}
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-500">{t('researchArea')}</Label>
                        <p className="text-gray-900">{project.research_area || t('notSpecified')}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-500">{t('owner')}</Label>
                        <p className="text-gray-900">{project.owner_name}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-500">{t('startDate')}</Label>
                        <p className="text-gray-900">{formatDate(project.start_date)}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-500">{t('expectedEnd')}</Label>
                        <p className="text-gray-900">{formatDate(project.expected_end_date)}</p>
                      </div>
                    </div>

                    {/* Progress */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-gray-500">{t('progress')}</Label>
                        <span className="text-sm text-gray-900 font-semibold">
                          {project.tasks && project.tasks.length > 0
                            ? Math.round((project.tasks.filter(t => t.status === 'completed').length / project.tasks.length) * 100)
                            : 0}%
                        </span>
                      </div>
                      <Progress 
                        value={project.tasks && project.tasks.length > 0
                          ? Math.round((project.tasks.filter(t => t.status === 'completed').length / project.tasks.length) * 100)
                          : 0} 
                        className="h-3"
                        indicatorClassName={
                          project.tasks && project.tasks.length > 0 && 
                          project.tasks.filter(t => t.status === 'completed').length === project.tasks.length
                            ? 'bg-green-500'
                            : project.tasks && project.tasks.filter(t => t.status === 'completed').length > 0
                            ? 'bg-blue-500'
                            : 'bg-amber-500'
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {project.tasks && project.tasks.length > 0 ? (
                          <>
                            {project.tasks.filter(t => t.status === 'completed').length} of {project.tasks.length} tasks completed
                            {project.tasks.filter(t => t.status === 'completed').length === project.tasks.length && (
                              <span className="ml-2 text-green-600 font-semibold">✓ All tasks complete!</span>
                            )}
                          </>
                        ) : (
                          'No tasks created yet'
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('quickActions')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-cyan-500 hover:bg-cyan-600">
                        <Upload className="mr-2 h-4 w-4" />
                        {t('uploadFiles')}
                      </Button>
                    </DialogTrigger>
                  </Dialog>

                  {canInviteCollaborators() && (
                    <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full">
                          <UserPlus className="mr-2 h-4 w-4" />
                          {t('inviteCollaborators')}
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                  )}

                  {/* Agreement Buttons */}
                  <Button 
                    variant="outline" 
                    className="w-full border-blue-200 hover:bg-blue-50 text-blue-700"
                    onClick={() => setNdaModalOpen(true)}
                  >
                    <FileSignature className="mr-2 h-4 w-4" />
                    {t('signNDA')}
                  </Button>

                  {isOwner && (
                    <Button 
                      variant="outline" 
                      className="w-full border-purple-200 hover:bg-purple-50 text-purple-700"
                      onClick={() => setRcaFormOpen(true)}
                    >
                      <FileSignature className="mr-2 h-4 w-4" />
                      {t('setupRCA')}
                    </Button>
                  )}

                  <Button 
                    variant="outline" 
                    className="w-full border-green-200 hover:bg-green-50 text-green-700"
                    onClick={() => setRcaModalOpen(true)}
                  >
                    <FileSignature className="mr-2 h-4 w-4" />
                    {t('signRCA')}
                  </Button>

                  <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">
                        <Plus className="mr-2 h-4 w-4" />
                        {t('createTask')}
                      </Button>
                    </DialogTrigger>
                  </Dialog>
                </CardContent>
              </Card>

              {/* Project Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('projectStatistics')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{t('collaborators')}</span>
                    </div>
                    <span className="font-semibold">{project.collaborators?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{t('files')}</span>
                    </div>
                    <span className="font-semibold">{project.files?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{t('tasks')}</span>
                    </div>
                    <span className="font-semibold">{project.tasks?.length || 0}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Tabbed Content */}
          <Tabs defaultValue="files" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="files">{t('files')}</TabsTrigger>
              <TabsTrigger value="documents">{t('documents')}</TabsTrigger>
              <TabsTrigger value="tasks">{t('tasks')}</TabsTrigger>
              <TabsTrigger value="team">{t('collaborators')}</TabsTrigger>
              <TabsTrigger value="agreements">{t('agreements')}</TabsTrigger>
              <TabsTrigger value="timeline">{t('timeline')}</TabsTrigger>
            </TabsList>

            <TabsContent value="files" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('projectFiles')}</CardTitle>
                    <Button 
                      onClick={() => setUploadDialogOpen(true)}
                      size="sm"
                      className="bg-cyan-500 hover:bg-cyan-600"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {t('upload')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.files?.length > 0 ? (
                    <div className="grid gap-4">
                      {project.files.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center space-x-4">
                            <div className="text-cyan-600">
                              {getFileIcon(file.file_type)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{file.original_filename}</p>
                              <p className="text-sm text-gray-500">
                                {formatFileSize(file.file_size)} • Uploaded by {file.uploaded_by_name} • 
                                {new Date(file.uploaded_at).toLocaleDateString()}
                              </p>
                              {file.description && (
                                <p className="text-sm text-gray-600 mt-1">{file.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handlePreviewFile(file)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadFile(file.id, file.original_filename)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleShareFile(file)}
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteFile(file.id, file.original_filename)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">No files uploaded yet</p>
                      <Button 
                        onClick={() => setUploadDialogOpen(true)}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload First File
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab - Preview Mode */}
            <TabsContent value="documents" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{t('projectDocuments')}</CardTitle>
                      <CardDescription>
                        {t('uploadPreviewDocuments')}
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setDocumentDialogOpen(true)}
                      size="sm"
                      className="bg-cyan-500 hover:bg-cyan-600"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t('uploadDocument')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No documents yet</p>
                      <p className="text-sm text-gray-400 mb-6">
                        Upload Word (.docx), Excel (.xlsx), or PowerPoint (.pptx) documents to share with your team
                      </p>
                      <Button 
                        onClick={() => setDocumentDialogOpen(true)}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Upload First Document
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-cyan-300"
                          onClick={() => handleOpenDocument(doc)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {doc.file_type === 'docx' || doc.file_type === 'doc' ? (
                                <FileText className="h-8 w-8 text-blue-500" />
                              ) : doc.file_type === 'xlsx' || doc.file_type === 'xls' ? (
                                <FileSpreadsheet className="h-8 w-8 text-green-500" />
                              ) : (
                                <File className="h-8 w-8 text-orange-500" />
                              )}
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {doc.file_type.toUpperCase()}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-sm mb-2 truncate" title={doc.title}>
                            {doc.title}
                          </h4>
                          <div className="text-xs text-gray-500 space-y-1">
                            <p>Updated: {new Date(doc.updated_at).toLocaleDateString()}</p>
                            <p>Versions: {doc.versions?.length || 1}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Document Preview Dialog */}
              {selectedDocument && (
                <DocumentPreviewDialog
                  isOpen={!!selectedDocument}
                  onClose={() => setSelectedDocument(null)}
                  document={selectedDocument}
                  userId={myProfile?.id}
                  userName={myProfile?.name || "User"}
                  onDocumentRename={handleDocumentRename}
                  onDocumentDelete={handleDocumentDeleteFromPreview}
                />
              )}
            </TabsContent>

            <TabsContent value="tasks" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('projectTasks')}</CardTitle>
                    <Button 
                      onClick={() => setTaskDialogOpen(true)}
                      size="sm"
                      className="bg-cyan-500 hover:bg-cyan-600"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t('addTask')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.tasks?.length > 0 ? (
                    <div className="space-y-4">
                      {project.tasks.map((task) => (
                        <div key={task.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start space-x-3">
                              {task.status === 'completed' ? (
                                <CheckSquare className="h-5 w-5 text-green-600 mt-0.5" />
                              ) : (
                                <Square className="h-5 w-5 text-gray-400 mt-0.5" />
                              )}
                              <div>
                                <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                  {task.title}
                                </h4>
                                {task.description && (
                                  <p className={`text-sm mt-1 ${task.status === 'completed' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {task.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {task.status !== 'completed' && (
                                <Button
                                  size="sm"
                                  onClick={() => toggleTaskCompletion(task.id, task.status)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  {t('done')}
                                </Button>
                              )}
                              {task.status === 'completed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => toggleTaskCompletion(task.id, task.status)}
                                  className="text-gray-600"
                                >
                                  <Play className="h-4 w-4 mr-1" />
                                  {t('reopen')}
                                </Button>
                              )}
                              {isProjectOwner() && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deleteTask(task.id, task.title)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title={t('deleteTask')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Badge className={getTaskStatusColor(task.status)}>
                                {t(task.status.replace('_', ' '))}
                              </Badge>
                              <Badge className={getPriorityColor(task.priority)}>
                                {t(task.priority)}
                              </Badge>
                            </div>
                          </div>
                          
                          {task.assigned_to_names?.length > 0 && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                              <Users className="h-4 w-4" />
                              <span>{t('assignTo')}: {task.assigned_to_names.join(', ')}</span>
                            </div>
                          )}
                          
                          {task.due_date && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <span>{t('due')}: {formatDate(task.due_date)}</span>
                            </div>
                          )}
                          
                          {task.completion_percentage > 0 && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-gray-600">Progress</span>
                                <span className="text-sm text-gray-900">{task.completion_percentage}%</span>
                              </div>
                              <Progress value={task.completion_percentage} className="h-2" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">No tasks created yet</p>
                      <Button 
                        onClick={() => setTaskDialogOpen(true)}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Create First Task
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="team" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('teamMembers')}</CardTitle>
                    {canInviteCollaborators() && (
                      <Button 
                        onClick={() => setInviteDialogOpen(true)}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        {t('invite')}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {project.collaborators?.map((collaborator) => (
                      <div key={collaborator.researcher_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-cyan-100 rounded-full flex items-center justify-center">
                            <span className="text-cyan-700 font-medium">
                              {collaborator.researcher_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{collaborator.researcher_name}</p>
                            <p className="text-sm text-gray-500">
                              {collaborator.role.charAt(0).toUpperCase() + collaborator.role.slice(1)} • 
                              {t('joined')} {new Date(collaborator.joined_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">
                            {collaborator.role === 'owner' ? t('owner') : 
                             collaborator.role === 'admin' ? t('editor') : 
                             t('collaborator')}
                          </Badge>
                          
                          {/* Owner-only controls */}
                          {isProjectOwner() && collaborator.role !== 'owner' && (
                            <>
                              <select
                                value={collaborator.role}
                                onChange={(e) => handleChangeCollaboratorRole(
                                  collaborator.researcher_id, 
                                  collaborator.researcher_name, 
                                  e.target.value
                                )}
                                className="text-sm border rounded px-2 py-1"
                              >
                                <option value="collaborator">{t('collaborator')}</option>
                                <option value="admin">{t('editor')}</option>
                              </select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveCollaborator(
                                  collaborator.researcher_id, 
                                  collaborator.researcher_name
                                )}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timeline" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('projectTimelineTaskProgress')}</CardTitle>
                  <CardDescription>
                    {t('visualRepresentation')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {project.tasks && project.tasks.length > 0 ? (
                    <div className="space-y-8">
                      {/* Task Completion Overview */}
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Pie Chart - Task Status Distribution */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">{t('taskStatusDistribution')}</h3>
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={[
                                  {
                                    name: t('completed'),
                                    value: project.tasks.filter(t => t.status === 'completed').length,
                                    fill: '#10b981'
                                  },
                                  {
                                    name: t('inProgress'),
                                    value: project.tasks.filter(t => t.status === 'in_progress').length,
                                    fill: '#3b82f6'
                                  },
                                  {
                                    name: t('pending'),
                                    value: project.tasks.filter(t => t.status === 'pending' || !t.status).length,
                                    fill: '#f59e0b'
                                  }
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                                outerRadius={80}
                                dataKey="value"
                              />
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Stats Summary */}
                        <div className="space-y-4">
                          <h3 className="text-lg font-semibold mb-4">{t('taskStatistics')}</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <CheckSquare className="h-5 w-5 text-green-600" />
                                <span className="font-medium text-green-900">{t('completed')}</span>
                              </div>
                              <span className="text-2xl font-bold text-green-600">
                                {project.tasks.filter(t => t.status === 'completed').length}
                              </span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Play className="h-5 w-5 text-blue-600" />
                                <span className="font-medium text-blue-900">{t('inProgress')}</span>
                              </div>
                              <span className="text-2xl font-bold text-blue-600">
                                {project.tasks.filter(t => t.status === 'in_progress').length}
                              </span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="h-5 w-5 text-amber-600" />
                                <span className="font-medium text-amber-900">{t('pending')}</span>
                              </div>
                              <span className="text-2xl font-bold text-amber-600">
                                {project.tasks.filter(t => t.status === 'pending' || !t.status).length}
                              </span>
                            </div>
                            <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg border-2 border-gray-300">
                              <div className="flex items-center gap-2">
                                <FileText className="h-5 w-5 text-gray-600" />
                                <span className="font-medium text-gray-900">{t('totalTasks')}</span>
                              </div>
                              <span className="text-2xl font-bold text-gray-900">
                                {project.tasks.length}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bar Chart - Task Progress by Name */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4">{t('taskProgressOverview')}</h3>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={project.tasks.map((task, index) => ({
                              name: task.title.length > 20 ? task.title.substring(0, 20) + '...' : task.title,
                              fullName: task.title,
                              completed: task.status === 'completed' ? 100 : 0,
                              inProgress: task.status === 'in_progress' ? 50 : 0,
                              pending: task.status === 'pending' || !task.status ? 25 : 0,
                              status: task.status || 'pending',
                              priority: task.priority || 'medium',
                              dueDate: task.due_date
                            }))}
                            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              angle={-45}
                              textAnchor="end"
                              height={100}
                              interval={0}
                            />
                            <YAxis 
                              label={{ value: 'Completion %', angle: -90, position: 'insideLeft' }}
                              domain={[0, 100]}
                            />
                            <Tooltip 
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white p-3 border rounded-lg shadow-lg">
                                      <p className="font-semibold mb-1">{data.fullName}</p>
                                      <p className="text-sm">Status: <span className={`font-medium ${
                                        data.status === 'completed' ? 'text-green-600' :
                                        data.status === 'in_progress' ? 'text-blue-600' :
                                        'text-amber-600'
                                      }`}>
                                        {data.status === 'completed' ? 'Completed' :
                                         data.status === 'in_progress' ? 'In Progress' :
                                         'Pending'}
                                      </span></p>
                                      <p className="text-sm">Priority: <span className="font-medium">{data.priority}</span></p>
                                      {data.dueDate && (
                                        <p className="text-sm">Due: {new Date(data.dueDate).toLocaleDateString()}</p>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Legend />
                            <Bar dataKey="completed" stackId="a" fill="#10b981" name={t('completed')} />
                            <Bar dataKey="inProgress" stackId="a" fill="#3b82f6" name={t('inProgress')} />
                            <Bar dataKey="pending" stackId="a" fill="#f59e0b" name={t('pending')} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Timeline View - Tasks List with Visual Progress */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4">{t('detailedTaskTimeline')}</h3>
                        <div className="space-y-3">
                          {project.tasks
                            .sort((a, b) => {
                              // Sort by due date if available
                              if (a.due_date && b.due_date) {
                                return new Date(a.due_date) - new Date(b.due_date);
                              }
                              return 0;
                            })
                            .map((task, index) => (
                            <div key={task.id} className="relative pl-8 pb-6">
                              {/* Timeline line */}
                              {index < project.tasks.length - 1 && (
                                <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-gray-300"></div>
                              )}
                              
                              {/* Timeline dot */}
                              <div className={`absolute left-0 top-1 w-6 h-6 rounded-full border-4 ${
                                task.status === 'completed' ? 'bg-green-500 border-green-200' :
                                task.status === 'in_progress' ? 'bg-blue-500 border-blue-200' :
                                'bg-amber-500 border-amber-200'
                              }`}></div>

                              {/* Task card */}
                              <div className={`border-2 rounded-lg p-4 ${
                                task.status === 'completed' ? 'bg-green-50 border-green-200' :
                                task.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                                'bg-amber-50 border-amber-200'
                              }`}>
                                <div className="flex justify-between items-start mb-2">
                                  <h4 className={`font-semibold ${
                                    task.status === 'completed' ? 'text-green-900 line-through' : 'text-gray-900'
                                  }`}>
                                    {task.title}
                                  </h4>
                                  <Badge variant={
                                    task.status === 'completed' ? 'default' :
                                    task.status === 'in_progress' ? 'secondary' :
                                    'outline'
                                  } className={
                                    task.status === 'completed' ? 'bg-green-500' :
                                    task.status === 'in_progress' ? 'bg-blue-500' :
                                    'bg-amber-500'
                                  }>
                                    {task.status === 'completed' ? `✓ ${t('completed')}` :
                                     task.status === 'in_progress' ? `↻ ${t('inProgress')}` :
                                     `⏱ ${t('pending')}`}
                                  </Badge>
                                </div>
                                {task.description && (
                                  <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                                )}
                                <div className="flex gap-4 text-xs text-gray-500">
                                  {task.priority && (
                                    <span className="flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      {t('priorityLabel')}: {task.priority}
                                    </span>
                                  )}
                                  {task.due_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {t('due')}: {new Date(task.due_date).toLocaleDateString()}
                                    </span>
                                  )}
                                  {task.assigned_to && task.assigned_to.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {task.assigned_to.length} assigned
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600 mb-2">No tasks created yet</p>
                      <p className="text-sm text-gray-500 mb-4">Create your first task to see the timeline visualization</p>
                      <Button 
                        onClick={() => setTaskDialogOpen(true)}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Create First Task
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Agreements Tab */}
            <TabsContent value="agreements" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileSignature className="h-5 w-5 text-blue-600" />
                    {t('agreements')}
                  </CardTitle>
                  <CardDescription>
                    {t('agreementsSigned')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* NDA Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-blue-700 mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        {t('nonDisclosureAgreement')} (NDA)
                      </h3>
                      {agreements.nda && agreements.nda.length > 0 ? (
                        <div className="space-y-2">
                          {agreements.nda.map((agreement) => (
                            <div key={agreement.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{agreement.signatory_name}</p>
                                <p className="text-sm text-gray-600">{agreement.signatory_email}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {t('signedOn')}: {new Date(agreement.signed_at).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-400 font-mono mt-1">
                                  {t('digitalFingerprint')}: {agreement.signature_hash.substring(0, 16)}...
                                </p>
                              </div>
                              <a
                                href={`${BACKEND_URL}/api/agreements/download/${agreement.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-4 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                PDF
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">{t('notSigned')}</p>
                      )}
                    </div>

                    {/* RCA Section */}
                    <div>
                      <h3 className="text-lg font-semibold text-purple-700 mb-3 flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        {t('researchCollaborationAgreement')} (RCA)
                      </h3>
                      {agreements.rca && agreements.rca.length > 0 ? (
                        <div className="space-y-2">
                          {agreements.rca.map((agreement) => (
                            <div key={agreement.id} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{agreement.signatory_name}</p>
                                <p className="text-sm text-gray-600">{agreement.signatory_email}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {t('signedOn')}: {new Date(agreement.signed_at).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-400 font-mono mt-1">
                                  {t('digitalFingerprint')}: {agreement.signature_hash.substring(0, 16)}...
                                </p>
                              </div>
                              <a
                                href={`${BACKEND_URL}/api/agreements/download/${agreement.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-4 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                PDF
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">{t('notSigned')}</p>
                      )}
                    </div>

                    {/* Info Box */}
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <strong>{t('note')}:</strong> {t('bothPartiesMustSign')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('uploadFiles')}</DialogTitle>
            <DialogDescription>
              {t('uploadResearchFiles')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('selectFiles')}</Label>
              <Input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.txt,.csv,.mp3,.mp4"
                onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                className="cursor-pointer"
              />
              {uploadFiles.length > 0 && (
                <div className="text-sm text-gray-600">
                  {t('selected')} {uploadFiles.length} {t('fileCount')}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('descriptionOptional')}</Label>
              <Textarea
                placeholder={t('briefDescriptionFiles')}
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                type="submit" 
                disabled={uploading || uploadFiles.length === 0}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600"
              >
                {uploading ? t('uploading') : t('uploadFiles')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setUploadDialogOpen(false)}
              >
                {t('cancel')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Collaborators Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('inviteCollaborators')}</DialogTitle>
            <DialogDescription>
              {t('inviteResearchersToCollaborate')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('selectResearchers')}</Label>
              <div className="border rounded-md p-4 max-h-[200px] overflow-y-auto space-y-2">
                {allResearchers
                  .filter(r => r.id !== myProfile?.id && !project.collaborators?.some(c => c.researcher_id === r.id))
                  .map(researcher => (
                    <div key={researcher.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        id={`collaborator-${researcher.id}`}
                        checked={selectedCollaborators.includes(researcher.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCollaborators([...selectedCollaborators, researcher.id]);
                          } else {
                            setSelectedCollaborators(selectedCollaborators.filter(id => id !== researcher.id));
                          }
                        }}
                        className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                      />
                      <label 
                        htmlFor={`collaborator-${researcher.id}`}
                        className="flex-1 cursor-pointer text-sm"
                      >
                        <div className="font-medium text-gray-900">{researcher.name}</div>
                        <div className="text-xs text-gray-500">{researcher.university}</div>
                      </label>
                    </div>
                  ))
                }
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('role')}</Label>
              <select 
                value={collaboratorRole}
                onChange={(e) => setCollaboratorRole(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="collaborator">{t('collaboratorRole')}</option>
                <option value="admin">{t('editor')} ({t('canManageTeam')})</option>
                <option value="viewer">{t('viewer')} ({t('canOnlyView')})</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleInviteCollaborators}
                disabled={selectedCollaborators.length === 0}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600"
              >
                {t('inviteCollaborators')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setInviteDialogOpen(false)}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('createTask')}</DialogTitle>
            <DialogDescription>
              {t('addNewTaskToTrack')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('taskTitle')} *</Label>
              <Input
                placeholder="e.g., Data collection and analysis"
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('taskDescription')}</Label>
              <Textarea
                placeholder={t('taskDescriptionPlaceholder')}
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('priority')}</Label>
                <select 
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({...taskForm, priority: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="low">{t('low')}</option>
                  <option value="medium">{t('medium')}</option>
                  <option value="high">{t('high')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t('dueDate')}</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleCreateTask}
                disabled={!taskForm.title}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600"
              >
                {t('createTask')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTaskDialogOpen(false)}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog - OnlyOffice */}
      <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('uploadDocument')}</DialogTitle>
            <DialogDescription>
              {t('uploadWordExcelPowerPoint')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('selectDocument')}</Label>
              <Input
                type="file"
                accept=".docx,.doc,.xlsx,.xls,.pptx,.ppt,.odt,.ods,.odp"
                onChange={(e) => setDocumentFile(e.target.files[0])}
                className="cursor-pointer"
              />
              {documentFile && (
                <div className="text-sm text-gray-600">
                  {t('selected')}: {documentFile.name}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleDocumentUpload} 
                disabled={uploadingDocument || !documentFile}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600"
              >
                {uploadingDocument ? t('uploading') : t('uploadDocument')}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setDocumentDialogOpen(false);
                  setDocumentFile(null);
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <FilePreviewDialog
        isOpen={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        file={selectedFile}
        projectId={projectId}
        myProfile={myProfile}
        onFileUpdate={fetchProject}
      />

      {/* File Share Dialog */}
      <FileShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        file={selectedFile}
        projectId={projectId}
        myProfile={myProfile}
        allResearchers={allResearchers}
      />

      {/* NDA Agreement Modal */}
      <AgreementModal
        isOpen={ndaModalOpen}
        onClose={() => setNdaModalOpen(false)}
        agreementType="NDA"
        projectId={projectId}
        projectTitle={project?.title || ''}
        userRole={isOwner ? 'owner' : 'collaborator'}
        agreementContent={ndaContent}
        onSigned={() => fetchAgreements()}
      />

      {/* RCA Form Modal (Owner Only) */}
      {isOwner && (
        <RCAFormModal
          isOpen={rcaFormOpen}
          onClose={() => setRcaFormOpen(false)}
          projectId={projectId}
          projectTitle={project?.title || ''}
          onTemplateSaved={() => {
            loadAgreementContent();
            alert(t('saveRCATemplate') + ' ✓');
          }}
        />
      )}

      {/* RCA Agreement Modal */}
      <AgreementModal
        isOpen={rcaModalOpen}
        onClose={() => setRcaModalOpen(false)}
        agreementType="RCA"
        projectId={projectId}
        projectTitle={project?.title || ''}
        userRole={isOwner ? 'owner' : 'collaborator'}
        agreementContent={rcaContent}
        onSigned={() => fetchAgreements()}
      />
    </div>
  );
};

export default ProjectDetail;